import {isString} from 'lodash';
import React, {FC} from 'react';

import {PopoverCoordinator} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {SearchHintItemDropdown} from './searchHintItemDropdown';
import {SearchHintItemPill} from './searchHintItemPill';
import {VisualSearchHint} from './searchHintsHelpers';

/*
 * Props.
 */

interface SearchHintItemProps {
  hint: VisualSearchHint;
  isDisabled: boolean;
  onClick: (hint: VisualSearchHint, selectedIndex?: number) => void;
}

/*
 * Component.
 */

export const SearchHintItem: FC<SearchHintItemProps> = (props) => {
  const {hint, isDisabled, onClick} = props;
  const {icon, values, selectedValueIndex} = hint;

  const isSelected = selectedValueIndex > -1 && !values[selectedValueIndex].isDefaultValue;

  const getSearchHintDisplayedValue = () => {
    // If one value is selected, we might want to display a special title.
    if (selectedValueIndex > -1) {
      const {modifier, title, value} = values[selectedValueIndex];
      return title || hint.title || `${modifier}.${value}`;
    }

    // Otherwise, fallback to hint's title.
    if (hint.title) {
      return hint.title;
    }

    // If hint.title is not defined, try to use first `<modifier>:<value>`.
    if (hint.values.length > 0) {
      const {modifier, value} = hint.values[0];
      return `${modifier}.${value}`;
    }

    // This should not happen.
    return '';
  };

  const displayedTitle = maybeTruncateValue(getSearchHintDisplayedValue());

  // If one value is selected, we might want to display a special icon.
  // Otherwise, fallback to hint's one.
  const displayedIcon = values[selectedValueIndex]?.icon || icon;

  const interactionId = `search-hint-${hint.type}`;

  if (values.length === 1) {
    return (
      <SearchHintItemPill
        title={displayedTitle}
        icon={displayedIcon}
        onClick={() => onClick(hint)}
        isSelected={isSelected}
        isDisabled={isDisabled}
        interactionId={interactionId}
      />
    );
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <SearchHintItemPill
          title={displayedTitle}
          icon={displayedIcon}
          isSelected={isSelected}
          isDisabled={isDisabled}
          isHovered={isOpen}
          interactionId={interactionId}
        />
      )}
      renderModal={(popoverProps) => (
        <SearchHintItemDropdown
          {...popoverProps}
          hint={hint}
          onItemClick={(index: number) => onClick(hint, index)}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

const maybeTruncateValue = (value: string, maxChars: number = 20) => {
  if (!value) {
    return null;
  }

  if (!isString(value)) {
    return value;
  }

  if (value.length <= maxChars) {
    return value;
  }

  return `${value.slice(0, maxChars)}…`;
};
